import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { NotificationProvider } from './context/NotificationContext'
import { AttendanceProvider } from './context/AttendanceContext'
import { SectionProvider } from './context/SectionContext'
import { CallScriptProvider } from '@/features/leads/CallScriptContext'
import AppLayout from './layouts/AppLayout'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import LandingPage from './pages/LandingPage'
import DashboardPage from './pages/DashboardPage'
import ProjectsPage from './pages/ProjectsPage'
import GoalsPage from './pages/GoalsPage'
import PortfolioTimelinePage from './pages/PortfolioTimelinePage'
import RecentPage from './pages/RecentPage'
import LeadsPage from './pages/LeadsPage'
import LeadsByAssigneePage from './pages/LeadsByAssigneePage'
import LeadsBySourcePage from './pages/LeadsBySourcePage'
import LeadsOverduePage from './pages/LeadsOverduePage'
import LeadsArchivePage from './pages/LeadsArchivePage'
import DealsPage from './pages/DealsPage'
import DealsPipelinePage from './pages/DealsPipelinePage'
import DealsTimelinePage from './pages/DealsTimelinePage'
import DealsArchivePage from './pages/DealsArchivePage'
import SalesDocsPage from './pages/SalesDocsPage'
import SalesDocDetailPage from '@/features/salesdocs/pages/SalesDocDetailPage'
import SettingsPage from './pages/SettingsPage'
import SettingsOverview from './pages/settings/SettingsOverview'
import EmailSettingsPage from './pages/settings/EmailSettingsPage'
import MasterDataLayout from './pages/admin/MasterDataLayout'
import HrSettingsLayout from './pages/admin/HrSettingsLayout'
import HrAdminRoute from './pages/admin/HrAdminRoute'
import AssetAllocationPage from './pages/admin/AssetAllocationPage'
import HrLeavePolicyPage from './pages/admin/HrLeavePolicyPage'
import HrHolidaysPage from './pages/admin/HrHolidaysPage'
import DepartmentsAdminPage from './pages/admin/DepartmentsAdminPage'
import RolesAdminPage from './pages/admin/RolesAdminPage'
import CompanyProfilePage from './pages/admin/CompanyProfilePage'
import CrmVocabularyAdminPage from './pages/admin/CrmVocabularyAdminPage'
import AssetsAdminPage from './pages/admin/AssetsAdminPage'
import AssetDetailPage from './pages/admin/AssetDetailPage'
import AssetMasterDataPage from './pages/admin/AssetMasterDataPage'
import ProductServicesAdminPage from './pages/admin/ProductServicesAdminPage'
import HsnSacAdminPage from './pages/admin/HsnSacAdminPage'
import TaxMasterAdminPage from './pages/admin/TaxMasterAdminPage'
import UnitMasterAdminPage from './pages/admin/UnitMasterAdminPage'
import PaymentTermAdminPage from './pages/admin/PaymentTermAdminPage'
import AssumptionExclusionAdminPage from './pages/admin/AssumptionExclusionAdminPage'
import TermConditionAdminPage from './pages/admin/TermConditionAdminPage'
import BankDetailAdminPage from './pages/admin/BankDetailAdminPage'
import DeclarationAdminPage from './pages/admin/DeclarationAdminPage'
import QuotationDefaultsAdminPage from './pages/admin/QuotationDefaultsAdminPage'
import QuotationDisplaySettingAdminPage from './pages/admin/QuotationDisplaySettingAdminPage'
import DocumentNumberingAdminPage from './pages/admin/DocumentNumberingAdminPage'
import PeoplePage from './pages/PeoplePage'
import EmployeeProfilePage from './pages/EmployeeProfilePage'
import GanttPage from './pages/GanttPage'
import TimesheetsPage from './pages/TimesheetsPage'
import ResourcesPage from './pages/ResourcesPage'
import PortfolioPage from './pages/PortfolioPage'
import { PermissionRoute } from './shared/components/PermissionRoute'
import { DashboardRoute } from './shared/components/DashboardRoute'

// Sales Pages
import { QuotationsPage } from './pages/sales/QuotationsPage'
import { InvoiceRequestsPage } from './pages/sales/InvoiceRequestsPage'
import { MilestoneDashboardPage } from './pages/sales/MilestoneDashboardPage'
import { ContactsPage } from './pages/sales/contacts/ContactsPage'
import { ContactDetailPage } from './pages/sales/contacts/ContactDetailPage'
import { ProformaInvoicesPage } from './pages/sales/ProformaInvoicesPage'
import { ProformaPage } from './pages/sales/ProformaPage'
import { TaxInvoicesPage } from './pages/sales/TaxInvoicesPage'
import { EstimateInvoicesPage } from './pages/sales/EstimateInvoicesPage'
import SalesScraperPage from './pages/sales/SalesScraperPage'
import DeletionApprovalsPage from './pages/sales/DeletionApprovalsPage'

// Migrated Spaces Suite Pages
import SpacesPage from './pages/spaces/SpacesListPage'
import SpaceLayout from './layouts/SpaceLayout'
import {
  SummaryPage,
  ListPage,
  BoardPage,
  BacklogPage,
  CalendarPage,
  TimelinePage,
  ReportsPage,
  DocsPage,
  AutomationPage,
  GithubPage,
  EmployeeDashboardPage,
} from './features/spaces/pages'
import SpaceSettingsPage from './pages/spaces/SpaceSettingsPage'

// ── HR / People section ────────────────────────────────────────────────────
import { HrDashboardPage } from '@/features/hr/pages/HrDashboardPage'
import { RecruitmentPage } from '@/features/recruitment/pages/RecruitmentPage'
import { OfferLetterPage } from '@/features/hr/pages/OfferLetterPage'
import { HrOnboardingPage } from '@/features/hr/pages/HrOnboardingPage'
import { HrDocumentViewerPage } from '@/features/hr/pages/HrDocumentViewerPage'
import { HrDocumentTemplatesPage } from '@/features/hr/pages/HrDocumentTemplatesPage'
import { HrBrandingPage } from '@/features/hr/pages/HrBrandingPage'
import { HrEmployeeCodesPage } from '@/features/hr/pages/HrEmployeeCodesPage'
import { LeavesPage } from '@/features/hr/pages/LeavesPage'
import { PerformancePage } from '@/features/hr/pages/PerformancePage'
import { HrPerformanceSettingsPage } from '@/features/hr/pages/HrPerformanceSettingsPage'
import { HrActivityTargetsPage } from '@/features/hr/pages/HrActivityTargetsPage'
import { IncentivesPage } from '@/features/hr/pages/IncentivesPage'
import { PayrollPage } from '@/features/hr/pages/PayrollPage'
import { LedgerPage } from '@/features/hr/pages/LedgerPage'
import { OrganizationPage } from '@/features/hr/pages/OrganizationPage'
import { EJoiningPage } from '@/features/hr/pages/EJoiningPage'
import { AttendancePage as HrAttendancePage } from '@/features/attendance/pages/AttendancePage'
import { BiometricAdminPage } from '@/features/attendance/pages/BiometricAdminPage'
import { ServiceDeskPage } from '@/features/service-desk/pages/ServiceDeskPage'
import { AnnouncementsPage } from '@/features/announcements/pages/AnnouncementsPage'
import { ChatPage } from '@/features/chat/pages/ChatPage'
import NotepadPage from './pages/NotepadPage'
import NoteDetailPage from './pages/NoteDetailPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <div className="min-h-screen bg-background" aria-busy="true" />
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}


export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AttendanceProvider>
          <SectionProvider>
            <CallScriptProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                {/* E-Joining is for new joiners who have no account yet: reached with the link alone. */}
                <Route path="/e-joining/:token" element={<EJoiningPage />} />

              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path="/app" element={<DashboardRoute><DashboardPage /></DashboardRoute>} />
                <Route path="/app/projects" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><ProjectsPage /></PermissionRoute>
                } />
                <Route path="/app/waterfall" element={
                  <PermissionRoute module="TASKS" action="VIEW"><GanttPage /></PermissionRoute>
                } />
                <Route path="/app/timesheets" element={
                  <PermissionRoute module="TIMESHEETS" action="VIEW"><TimesheetsPage /></PermissionRoute>
                } />
                <Route path="/app/goals" element={
                  <PermissionRoute module="TASKS" action="VIEW"><GoalsPage /></PermissionRoute>
                } />
                <Route path="/app/resources" element={
                  <PermissionRoute module="TASKS" action="VIEW"><ResourcesPage /></PermissionRoute>
                } />
                <Route path="/app/portfolio" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><PortfolioPage /></PermissionRoute>
                } />
                <Route path="/app/portfolio-timeline" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><PortfolioTimelinePage /></PermissionRoute>
                } />
                <Route path="/app/organizations/:orgId/portfolio-timeline" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><PortfolioTimelinePage /></PermissionRoute>
                } />
                <Route path="/organizations/:orgId/portfolio-timeline" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><PortfolioTimelinePage /></PermissionRoute>
                } />
                <Route path="/portfolio-timeline" element={
                  <PermissionRoute module="PROJECTS" action="VIEW"><PortfolioTimelinePage /></PermissionRoute>
                } />
                <Route path="/app/recent" element={
                  <PermissionRoute module="DASHBOARD" action="VIEW"><RecentPage /></PermissionRoute>
                } />
                {/* GoodDay Leads routes */}
                <Route path="/app/leads" element={
                  <PermissionRoute module="LEADS" action="VIEW"><LeadsPage /></PermissionRoute>
                } />
                <Route path="/app/leads/assignee" element={
                  <PermissionRoute module="LEADS" action="VIEW" minRole="ADMIN"><LeadsByAssigneePage /></PermissionRoute>
                } />
                <Route path="/app/leads/overdue" element={
                  <PermissionRoute module="LEADS" action="VIEW"><LeadsOverduePage /></PermissionRoute>
                } />
                <Route path="/app/leads/source" element={
                  <PermissionRoute module="LEADS" action="VIEW"><LeadsBySourcePage /></PermissionRoute>
                } />
                <Route path="/app/leads/archive" element={
                  <PermissionRoute module="LEADS" action="VIEW"><LeadsArchivePage /></PermissionRoute>
                } />
                {/* Alias so the Sales > Leads nav entry lands on the working leads page */}
                <Route path="/sales/leads" element={
                  <Navigate to="/app/leads" replace />
                } />
                {/* GoodDay Deals routes */}
                <Route path="/app/deals" element={
                  <PermissionRoute module="DEALS" action="VIEW"><DealsPage /></PermissionRoute>
                } />
                <Route path="/app/deals/pipeline" element={
                  <PermissionRoute module="DEALS" action="VIEW"><DealsPipelinePage /></PermissionRoute>
                } />
                <Route path="/app/deals/timeline" element={
                  <PermissionRoute module="DEALS" action="VIEW"><DealsTimelinePage /></PermissionRoute>
                } />
                <Route path="/app/deals/archive" element={
                  <PermissionRoute module="DEALS" action="VIEW"><DealsArchivePage /></PermissionRoute>
                } />
                <Route path="/sales-docs" element={
                  <PermissionRoute module="SALES_DOCS" action="VIEW"><SalesDocsPage /></PermissionRoute>
                } />
                <Route path="/sales-docs/:id" element={
                  <PermissionRoute module="SALES_DOCS" action="VIEW"><SalesDocDetailPage /></PermissionRoute>
                } />
                {/* Sales Routes */}
                <Route path="/app/sales/quotations" element={
                  <PermissionRoute module={['QUOTATIONS', 'SALES_DOCS']} action="VIEW"><QuotationsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/tax-invoices" element={
                  <PermissionRoute module={['INVOICE_REQUESTS', 'SALES_DOCS']} action="VIEW"><TaxInvoicesPage /></PermissionRoute>
                } />
                <Route path="/app/sales/estimate-invoices" element={
                  <PermissionRoute module={['INVOICE_REQUESTS', 'SALES_DOCS']} action="VIEW"><EstimateInvoicesPage /></PermissionRoute>
                } />
                <Route path="/app/sales/invoice-requests" element={
                  <PermissionRoute module="INVOICE_REQUESTS" action="VIEW"><InvoiceRequestsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/milestones" element={
                  <PermissionRoute module={['ACCOUNTS', 'SALES_DOCS', 'QUOTATIONS', 'INVOICE_REQUESTS']} action="VIEW"><MilestoneDashboardPage /></PermissionRoute>
                } />
                <Route path="/accounting-v2/milestones" element={
                  <PermissionRoute module={['ACCOUNTS', 'SALES_DOCS', 'QUOTATIONS', 'INVOICE_REQUESTS']} action="VIEW"><MilestoneDashboardPage /></PermissionRoute>
                } />
                <Route path="/app/sales/proforma-invoices" element={
                  <PermissionRoute module={['PROFORMA_INVOICES', 'SALES_DOCS']} action="VIEW"><InvoiceRequestsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/proforma-invoice" element={
                  <PermissionRoute module={['PROFORMA_INVOICES', 'SALES_DOCS']} action="VIEW"><InvoiceRequestsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/proforma/create" element={
                  <PermissionRoute module={['PROFORMA_INVOICES', 'SALES_DOCS']} action="CREATE"><ProformaPage /></PermissionRoute>
                } />
                <Route path="/app/sales/proforma/:id/edit" element={
                  <PermissionRoute module={['PROFORMA_INVOICES', 'SALES_DOCS']} action="VIEW"><ProformaPage /></PermissionRoute>
                } />
                <Route path="/app/sales/contacts" element={
                  <PermissionRoute module="SALES_DOCS" action="VIEW"><ContactsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/contacts/:id" element={
                  <PermissionRoute module="SALES_DOCS" action="VIEW"><ContactDetailPage /></PermissionRoute>
                } />
                <Route path="/app/sales/deletion-approvals" element={
                  <PermissionRoute module={['LEADS', 'DEALS']} action="DELETE" minRole="SUPERADMIN"><DeletionApprovalsPage /></PermissionRoute>
                } />
                <Route path="/app/sales/lead-scraper" element={
                  <PermissionRoute module="LEADS" action="VIEW" excludeDepartments={['accounts', 'finance']}><SalesScraperPage /></PermissionRoute>
                } />
                <Route path="/app/people" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><PeoplePage /></PermissionRoute>
                } />
                <Route path="/app/profile" element={<EmployeeProfilePage />} />
                <Route path="/app/profile/:id" element={<EmployeeProfilePage />} />
                <Route path="/app/my-profile" element={<Navigate to="/app/profile" replace />} />
                <Route path="/app/employees/:id" element={<EmployeeProfilePage />} />
                <Route path="/app/employees" element={<Navigate to="/app/people" replace />} />
                <Route path="/app/settings" element={<SettingsPage />}>
                  {/* The shell above is open to any authenticated user - My
                      Account/Email Settings below needs to be reachable by
                      every sales rep, not just admins. Every other tab here
                      keeps its own SUPERADMIN-only guard individually
                      (moved down from the single blanket guard this route
                      used to have) rather than gating the whole page. */}
                  <Route index element={<SettingsOverview />} />
                  <Route path="email" element={
                    // DASHBOARD.VIEW is granted to every role bundle - this
                    // is just "must be logged in", no minRole restriction.
                    <PermissionRoute module="DASHBOARD" action="VIEW"><EmailSettingsPage /></PermissionRoute>
                  } />
                  <Route path="departments" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><DepartmentsAdminPage /></PermissionRoute>
                  } />
                  <Route path="roles" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><RolesAdminPage /></PermissionRoute>
                  } />
                  <Route path="company" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><CompanyProfilePage /></PermissionRoute>
                  } />
                  <Route path="crm-vocabulary" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><CrmVocabularyAdminPage /></PermissionRoute>
                  } />
                  <Route path="assets" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><AssetsAdminPage /></PermissionRoute>
                  }>
                    <Route path=":id" element={
                      <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><AssetDetailPage /></PermissionRoute>
                    } />
                  </Route>
                  {/* Device allocation: super admins and HR Admins (the guard checks). */}
                  <Route path="asset-allocation" element={
                    <PermissionRoute module="DASHBOARD" action="VIEW"><HrAdminRoute><AssetAllocationPage /></HrAdminRoute></PermissionRoute>
                  } />
                  <Route path="master-data" element={
                    <PermissionRoute module="USERS" action="EDIT" minRole="SUPERADMIN"><MasterDataLayout /></PermissionRoute>
                  }>
                    <Route index element={<Navigate to="products-services" replace />} />
                    <Route path="products-services" element={<ProductServicesAdminPage />} />
                    <Route path="hsn-sac" element={<HsnSacAdminPage />} />
                    <Route path="taxes" element={<TaxMasterAdminPage />} />
                    <Route path="units" element={<UnitMasterAdminPage />} />
                    <Route path="payment-terms" element={<PaymentTermAdminPage />} />
                    <Route path="assumptions-exclusions" element={<AssumptionExclusionAdminPage />} />
                    <Route path="terms-conditions" element={<TermConditionAdminPage />} />
                    <Route path="bank-details" element={<BankDetailAdminPage />} />
                    <Route path="declarations" element={<DeclarationAdminPage />} />
                    <Route path="quotation-defaults" element={<QuotationDefaultsAdminPage />} />
                    <Route path="display-settings" element={<QuotationDisplaySettingAdminPage />} />
                    <Route path="document-numbering" element={<DocumentNumberingAdminPage />} />
                    <Route path="asset-master-data" element={<AssetMasterDataPage />} />
                  </Route>
                  {/* HR settings: super admins and Admins of the HR department (the layout checks). */}
                  <Route path="hr" element={
                    <PermissionRoute module="DASHBOARD" action="VIEW"><HrSettingsLayout /></PermissionRoute>
                  }>
                    <Route index element={<Navigate to="leave-types" replace />} />
                    <Route path="leave-types" element={<HrLeavePolicyPage />} />
                    <Route path="holidays" element={<HrHolidaysPage />} />
                    <Route path="branding" element={<HrBrandingPage />} />
                    <Route path="templates" element={<HrDocumentTemplatesPage />} />
                    <Route path="employee-codes" element={<HrEmployeeCodesPage />} />
                    <Route path="performance" element={<HrPerformanceSettingsPage />} />
                    <Route path="activity-targets" element={<HrActivityTargetsPage />} />
                  </Route>
                </Route>
                <Route path="/app/spaces" element={<Navigate to="/spaces" replace />} />

                {/* ── HR / People routes ───────────────────────────────────── */}
                <Route path="/app/hr/dashboard" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><HrDashboardPage /></PermissionRoute>
                } />
                <Route path="/app/hr/recruitment" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><RecruitmentPage /></PermissionRoute>
                } />
                <Route path="/app/hr/offer-letters" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><OfferLetterPage /></PermissionRoute>
                } />
                <Route path="/app/hr/onboarding" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><HrOnboardingPage /></PermissionRoute>
                } />
                <Route path="/app/hr/onboarding/:candidateId/documents/:docCode" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><HrDocumentViewerPage /></PermissionRoute>
                } />
                {/* Moved to Settings > HR; old addresses and bookmarks land there. */}
                <Route path="/app/hr/document-templates" element={<Navigate to="/app/settings/hr/templates" replace />} />
                <Route path="/app/hr/branding" element={<Navigate to="/app/settings/hr/branding" replace />} />
                <Route path="/app/hr/settings/performance" element={<Navigate to="/app/settings/hr/performance" replace />} />
                <Route path="/app/hr/performance" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><PerformancePage /></PermissionRoute>
                } />
                <Route path="/app/hr/incentives" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><IncentivesPage /></PermissionRoute>
                } />
                <Route path="/app/hr/payroll" element={
                  <PermissionRoute module={['PEOPLE', 'ACCOUNTS']} action="VIEW"><PayrollPage /></PermissionRoute>
                } />
                <Route path="/app/hr/ledger" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><LedgerPage /></PermissionRoute>
                } />
                <Route path="/app/hr/organization" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><OrganizationPage /></PermissionRoute>
                } />

                {/* ── Leaves ─────────────────────────────────────────────── */}
                <Route path="/app/leaves" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><LeavesPage /></PermissionRoute>
                } />

                {/* ── Attendance (People-scoped) ───────────────────────────── */}
                <Route path="/app/attendance" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><HrAttendancePage /></PermissionRoute>
                } />
                <Route path="/app/attendance/biometric" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><BiometricAdminPage /></PermissionRoute>
                } />

                {/* ── Service Desk ─────────────────────────────────────────── */}
                {/* Everyone signed in can raise and follow their own tickets; the server decides the rest. */}
                <Route path="/app/service-desk" element={<ServiceDeskPage />} />

                {/* ── Announcements ───────────────────────────────────────── */}
                <Route path="/app/announcements" element={
                  <PermissionRoute module="PEOPLE" action="VIEW"><AnnouncementsPage /></PermissionRoute>
                } />

                {/* ── Chat ───────────────────────────────────────────── */}
                {/* Everyone signed in can reach chat; the server decides which
                    conversations exist for them. */}
                <Route path="/app/chat" element={<ChatPage />} />

                {/* ── Notepad ───────────────────────────────────────────── */}
                <Route path="/app/notepad" element={
                  <PermissionRoute module="NOTES" action="VIEW"><NotepadPage /></PermissionRoute>
                } />
                <Route path="/app/notepad/:noteId" element={
                  <PermissionRoute module="NOTES" action="VIEW"><NoteDetailPage /></PermissionRoute>
                } />

                {/* Migrated Spaces Suite inside AppLayout for persistent sidebar */}
                <Route path="/spaces" element={
                  <PermissionRoute module="SPACES" action="VIEW"><SpacesPage /></PermissionRoute>
                } />
                <Route path="/spaces/:id" element={
                  <PermissionRoute module="SPACES" action="VIEW"><SpaceLayout /></PermissionRoute>
                }>
                  <Route index element={<Navigate to="summary" replace />} />
                  <Route path="summary" element={<SummaryPage />} />
                  <Route path="list" element={<ListPage />} />
                  <Route path="board" element={<BoardPage />} />
                  <Route path="backlog" element={<BacklogPage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="timeline" element={<TimelinePage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="employee-dashboard" element={<EmployeeDashboardPage />} />
                  <Route path="docs" element={<DocsPage />} />
                  <Route path="automation" element={<AutomationPage />} />
                  <Route path="github" element={<GithubPage />} />
                  <Route path="settings" element={<SpaceSettingsPage />} />
                </Route>
              </Route>

            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
          </CallScriptProvider>
        </SectionProvider>
      </AttendanceProvider>
    </NotificationProvider>
  </AuthProvider>
)
}
